import {
    Box,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    EmptyState,
    FilterBar,
    FlussPageHeader,
    FlussStatusChip,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { useFlussCable } from "modules/fluss/hooks/useFlussCable";
import { flussApi } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";

const taskKeys = { all: ["fluss", "tasks"] as const };

const FlussInboxPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const tasks = useQuery({
        queryKey: taskKeys.all,
        queryFn: () => flussApi.listTasks(),
        refetchOnMount: "always",
        refetchOnWindowFocus: "always",
        refetchInterval: 10000,
    });

    useFlussCable({
        inbox: true,
        onReceived: () => queryClient.invalidateQueries({ queryKey: taskKeys.all }),
    });

    const filtered = useMemo(() => {
        const withLabel = (tasks.data || []).map((task) => ({
            task,
            definitionLabel: flussDefinitionLabel(t, task.definition_name),
        }));
        const term = search.trim().toLocaleLowerCase();
        if (!term) return withLabel;
        return withLabel.filter(({ task, definitionLabel }) =>
            [task.title, definitionLabel, task.requester?.name]
                .filter(Boolean)
                .some((value) => String(value).toLocaleLowerCase().includes(term))
        );
    }, [search, tasks.data, t]);

    const formatDate = (value?: string | null) =>
        value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";

    return (
        <Box className="fluss-page">
            <Stack gap={2}>
                <FlussPageHeader
                    title={t("fluss.inbox.title", { defaultValue: "My tasks" })}
                    subtitle={t("fluss.inbox.subtitle", { defaultValue: "Work assigned to you across active processes." })}
                    status={<Chip size="small" label={t("fluss.inbox.open_count", { count: tasks.data?.length || 0, defaultValue: "{{count}} open" })} />}
                />
                <FilterBar search={search} onSearch={setSearch} />
                {!tasks.isLoading && filtered.length === 0 ? (
                    <EmptyState
                        title={t("fluss.inbox.empty_title_v2", { defaultValue: "No tasks assigned to you." })}
                        description={t("fluss.inbox.empty_description_v2", { defaultValue: "Tasks will appear here when a process needs your action." })}
                        actionLabel={t("fluss.inbox.empty_action", { defaultValue: "My requests" })}
                        onAction={() => navigate("/home/fluss/requests")}
                    />
                ) : (
                    <Paper variant="outlined">
                        <List disablePadding>
                            {filtered.map(({ task, definitionLabel }) => (
                                <ListItemButton
                                    key={task.id}
                                    divider
                                    onClick={() => navigate(`/home/fluss/inbox/${task.id}`)}
                                >
                                    <ListItemText
                                        primary={task.title}
                                        secondary={
                                            <Stack component="span" direction={{ xs: "column", sm: "row" }} gap={{ xs: 0, sm: 2 }}>
                                                {task.anchor?.display && <Typography component="span" variant="body2" color="text.secondary">{task.anchor.display}</Typography>}
                                                <Typography component="span" variant="body2">
                                                    {definitionLabel}
                                                </Typography>
                                                <Typography component="span" variant="body2" color="text.secondary">
                                                    {t("fluss.inbox.requester", { name: task.requester?.name || "—", defaultValue: "Requester: {{name}}" })}
                                                </Typography>
                                                <Typography component="span" variant="body2" color="text.secondary">
                                                    {t("fluss.inbox.due", { date: formatDate(task.due_at), defaultValue: "Due: {{date}}" })}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                    <FlussStatusChip status={task.status} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                )}
            </Stack>
        </Box>
    );
};

export default FlussInboxPage;
